import { useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';

/**
 * Return type for useDrawerRoute hook
 */
export interface UseDrawerRouteReturn {
  /** Resource ID from URL param, null if not present */
  resourceId: string | null;
  /** True if resourceId is present (drawer should be open) */
  isOpen: boolean;
  /** Navigate to basePath/:id (preserves query params) */
  open: (id: string) => void;
  /** Navigate back to basePath (preserves query params) */
  close: () => void;
}

/**
 * Options for useDrawerRoute hook
 */
export interface UseDrawerRouteOptions {
  /**
   * Name of the route param to use for the resource ID.
   * If not provided, it will be inferred from basePath:
   * '/products/pos' -> 'productId' (last segment + 'Id')
   */
  paramName?: string;
  /**
   * Optional suffix to append after the resource ID.
   * e.g., suffix: 'details' -> basePath/:id/details
   */
  suffix?: string;
}

/**
 * Extract the param name from a base path
 * '/products/pos' -> 'productId'
 * '/recipes' -> 'recipeId'
 */
function inferParamName(basePath: string): string {
  const segments = basePath.split('/').filter(Boolean);
  const lastSegment = segments[segments.length - 1] || 'resource';

  // Handle plural forms: 'products' -> 'product', 'pos' -> 'pos'
  let singular = lastSegment;
  if (singular.endsWith('s') && !singular.endsWith('ss') && singular.length > 1) {
    singular = singular.slice(0, -1);
  }

  return `${singular}Id`;
}

/**
 * Hook for managing drawer open/close state via URL path parameter.
 *
 * This hook enables deep linking for drawer/modal states by using URL paths
 * instead of local state. The drawer is considered "open" when a resource ID
 * is present in the URL.
 *
 * Features:
 * - Uses useParams() to read the resource ID
 * - Preserves all query params when opening/closing
 * - Infers param name from basePath or accepts custom paramName
 *
 * @param basePath - The base path for the route (e.g., '/products/pos')
 * @param options - Optional configuration
 *
 * @example
 * ```tsx
 * // Assumes route is /products/pos/:productId (optional)
 * const drawer = useDrawerRoute('/products/pos');
 *
 * // drawer.resourceId = 'abc123' if URL is /products/pos/abc123
 * // drawer.isOpen = true when resourceId is present
 * // drawer.open('xyz789') navigates to /products/pos/xyz789?...currentParams
 * // drawer.close() navigates to /products/pos?...currentParams
 * ```
 */
export function useDrawerRoute(
  basePath: string,
  options: UseDrawerRouteOptions = {}
): UseDrawerRouteReturn {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Determine the param name and suffix
  const paramName = options.paramName || inferParamName(basePath);
  const suffix = options.suffix;

  // Get resource ID from URL params
  const resourceId = useMemo(() => {
    const id = params[paramName];
    return id ?? null;
  }, [params, paramName]);

  // Drawer is open when resourceId is present AND path matches the suffix (if specified)
  const isOpen = useMemo(() => {
    if (resourceId === null) return false;
    if (!suffix) return true;
    // Check if the path ends with the suffix
    return location.pathname.endsWith(`/${suffix}`);
  }, [resourceId, suffix, location.pathname]);

  // Build URL with preserved query params
  const buildUrl = useCallback((path: string): string => {
    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  }, [searchParams]);

  // Open drawer by navigating to basePath/:id or basePath/:id/suffix
  const open = useCallback((id: string) => {
    const path = suffix ? `${basePath}/${id}/${suffix}` : `${basePath}/${id}`;
    const url = buildUrl(path);
    navigate(url);
  }, [basePath, suffix, buildUrl, navigate]);

  // Close drawer by navigating back to basePath
  const close = useCallback(() => {
    const url = buildUrl(basePath);
    navigate(url);
  }, [basePath, buildUrl, navigate]);

  return {
    resourceId,
    isOpen,
    open,
    close,
  };
}
